import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DialogContentText } from '@mui/material';

import * as routes from 'router/routes';
import { PROJECT_TYPES } from 'constants/commonProjects';
import { ORGANISATIONS_STATUSES } from 'constants/organisations';
import { notifyApiError } from 'helper/apiErrorNotification';
import { useDialog } from 'hooks/useDialog';
import { useCurrentUserInfo } from 'hooks/useCurrentUserInfo';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { useDuplicateFrameworkMutation } from 'store/entities/feedbackFramework/api';
import { useGetOrganisationsQuery } from 'store/entities/organisations/api';
import { useDuplicateProjectMutation } from 'store/entities/projects/api';
import { Select } from 'components/simple';
import { Dialog } from 'components/complex';

const id = 'duplicate-project-dialog';
const styles = {
  dialogContentStyles: {
    height: { xs: 130, md: 'fit-content' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: 4, md: 8 },
  },
  select: {
    flex: 'none',
    maxWidth: 285,
  },
  selectMenu: {
    width: '100%',
    maxWidth: 265,
  },
  selectOptionText: ({ palette }) => ({
    color: palette.colors.darkGrey,
  }),
};

export const DuplicateProjectDialog = () => {
  const { closeDialog } = useDialog();
  const { project = {} } = useSelector(dialogPropsSelector);

  const [selectedOrgId, setSelectedOrgId] = useState(project.organisationId || '');

  const { isSuperAdminPermissions } = useCurrentUserInfo(project.organisationId);
  const { data: organisations = [] } = useGetOrganisationsQuery(undefined, {
    skip: !isSuperAdminPermissions,
  });

  const [duplicateFramework, { isLoading: isFrameworkLoading }] = useDuplicateFrameworkMutation();
  const [duplicateProject, { isLoading: isProjectLoading }] = useDuplicateProjectMutation();

  const isLoading = isFrameworkLoading || isProjectLoading;

  const { push } = useHistory();

  const selectOptions = organisations.length
    ? organisations
        .filter(({ status }) => status !== ORGANISATIONS_STATUSES.ARCHIVED)
        .map(({ id: organisationId, organisationName }) => ({
          value: organisationId,
          label: organisationName,
        }))
    : [];

  const handleDuplicateProject = async () => {
    const payload = {
      id: project.id,
      selectedOrganisation: selectedOrgId,
    };

    const isCultureProject = project.projectType === PROJECT_TYPES.CULTURE_PROJECT;
    const duplicate = isCultureProject ? duplicateProject : duplicateFramework;
    const route = isCultureProject ? routes.newCulturalProject : routes.newFeedbackFramework;

    try {
      const duplicatedId = await duplicate(payload).unwrap();

      push(`/${selectedOrgId}/${route}/${duplicatedId}`, { isDuplicated: true });
      closeDialog();
    } catch (error) {
      notifyApiError(error);
    }
  };

  return (
    <Dialog
      isLoading={isLoading}
      id={id}
      title={`Are you sure you want to duplicate ${project.name}?`}
      onConfirm={handleDuplicateProject}
      confirmLabel="Duplicate"
      cancelLabel="Cancel"
      customStyles={{ contentStyles: styles.dialogContentStyles }}
    >
      {!!selectOptions.length && (
        <>
          <DialogContentText id={`${id}-description`} variant="body2" color="text.primary">
            Please select which organisation you would like to copy this project to:
          </DialogContentText>

          <Select
            onChange={setSelectedOrgId}
            value={selectedOrgId}
            options={selectOptions}
            customStyles={{
              select: styles.select,
              menu: styles.selectMenu,
              menuItem: styles.selectOptionText,
            }}
          />
        </>
      )}
    </Dialog>
  );
};
